'use client'

import { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react'
import { cn } from '../lib/utils'

/**
 * F3.5 — Canvas-based signature pad voor digitale ondertekening van het
 * onboarding-contract.
 *
 * Pointer Events ipv. afzonderlijke mouse/touch handlers — werkt op telefoon
 * (touch + Apple Pencil), trackpad-muis en digitale pen onder Windows.
 *
 * Why expliciet `useImperativeHandle` voor `clear`/`toDataURL`/`isEmpty`:
 * de wrapper-component hoeft de canvas-state niet te kennen, maar moet wel
 * `toDataURL()` kunnen aanroepen vanuit een `onClick`-handler op de submit-knop.
 * React's ref + imperatieve API past hier beter dan een controlled value.
 *
 * batch-68 handtekening (FO-345 §3.1) — de ENIGE tekenvak-component van de app
 * (de portal-eigen `SignatureCanvas` is hierop geconsolideerd):
 *  - streken worden bewaard, dus "Ongedaan maken" (laatste streek) werkt en een
 *    draaiing van de telefoon tekent alles opnieuw op de nieuwe breedte;
 *  - `aantalStreken()` en `inktBreedte()` voeden de leeg-check;
 *  - optioneel een grijze basislijn en het label "Teken hier je handtekening"
 *    (als HTML over het canvas, dus nooit in de geëxporteerde PNG);
 *  - devicePixelRatio-scherp; de export is een transparante PNG.
 * Bestaande gebruikers merken niets: de defaults zijn het oude gedrag.
 */
export interface SignaturePadHandle {
  clear: () => void
  toDataURL: () => string
  isEmpty: () => boolean
  /** batch-68 — haal de laatste streek weg. */
  undo?: () => void
  /** batch-68 — aantal streken (voor de kwaliteitscheck). */
  aantalStreken?: () => number
  /** batch-68 — breedte van de inkt in CSS-pixels (0 = leeg). */
  inktBreedte?: () => number
}

export interface SignaturePadProps {
  /** Pixel-hoogte van het canvas. Default 180 — leesbaar op iPhone-mini. */
  height?: number
  /** Pen-kleur. Default brand-donker `#1A1A18`. */
  strokeColor?: string
  /** Lijn-dikte; iets dikker maakt mobiele handtekeningen leesbaarder. */
  strokeWidth?: number
  className?: string
  onChange?: (isEmpty: boolean) => void
  /** batch-68 — grijze basislijn + label in het vak (default uit = oud gedrag). */
  basislijn?: boolean
  /** batch-68 — label in het lege vak, bv. "Teken hier je handtekening". */
  label?: string
  /** batch-68 — de eigen voetbalk met "Wissen" tonen (default aan = oud gedrag). */
  toonVoetbalk?: boolean
  /** batch-68 — uitgeschakeld (bv. tijdens verzenden). */
  disabled?: boolean
  /** batch-68 — toegankelijke naam van het tekenvak. */
  ariaLabel?: string
}

type Punt = { x: number; y: number }

export const SignaturePad = forwardRef<SignaturePadHandle, SignaturePadProps>(function SignaturePad(
  {
    height = 180,
    strokeColor = '#1A1A18',
    strokeWidth = 2,
    className,
    onChange,
    basislijn = false,
    label,
    toonVoetbalk = true,
    disabled = false,
    ariaLabel = 'Tekenvak voor je handtekening',
  },
  ref,
) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawingRef = useRef(false)
  const strekenRef = useRef<Punt[][]>([])
  const breedteRef = useRef(0)
  const [, forceRerender] = useState(0)

  /** Teken alle bewaarde streken opnieuw (na resize of ongedaan maken). */
  const tekenAlles = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.save()
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    ctx.restore()
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = strokeColor
    ctx.lineWidth = strokeWidth
    for (const streek of strekenRef.current) {
      if (streek.length === 0) continue
      ctx.beginPath()
      const eerste = streek[0] as Punt
      ctx.moveTo(eerste.x, eerste.y)
      if (streek.length === 1) ctx.lineTo(eerste.x + 0.1, eerste.y + 0.1)
      for (let i = 1; i < streek.length; i += 1) {
        const p = streek[i] as Punt
        ctx.lineTo(p.x, p.y)
      }
      ctx.stroke()
    }
  }, [strokeColor, strokeWidth])

  // High-DPI: schaal canvas-resolutie naar devicePixelRatio voor scherpe lijnen.
  // Bij een andere breedte (draaien naar liggend) worden de streken
  // meegeschaald en opnieuw getekend.
  const pasAfmetingAan = useCallback(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ratio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1
    const cssWidth = canvas.clientWidth
    if (cssWidth <= 0) return
    const oud = breedteRef.current
    if (oud > 0 && oud !== cssWidth) {
      const factor = cssWidth / oud
      strekenRef.current = strekenRef.current.map((s) => s.map((p) => ({ x: p.x * factor, y: p.y })))
    }
    breedteRef.current = cssWidth
    canvas.width = Math.round(cssWidth * ratio)
    canvas.height = Math.round(height * ratio)
    canvas.style.height = `${height}px`
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0)
    }
    tekenAlles()
  }, [height, tekenAlles])

  useEffect(() => {
    pasAfmetingAan()
    const canvas = canvasRef.current
    if (!canvas || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => pasAfmetingAan())
    ro.observe(canvas)
    return () => ro.disconnect()
  }, [pasAfmetingAan])

  function getPoint(e: React.PointerEvent<HTMLCanvasElement>): Punt {
    const rect = e.currentTarget.getBoundingClientRect()
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
  }

  function meldWijziging() {
    onChange?.(strekenRef.current.length === 0)
    forceRerender((n) => n + 1)
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    if (disabled) return
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    drawingRef.current = true
    const punt = getPoint(e)
    const leegVoorheen = strekenRef.current.length === 0
    strekenRef.current = [...strekenRef.current, [punt]]
    tekenAlles()
    if (leegVoorheen) meldWijziging()
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawingRef.current) return
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    const streek = strekenRef.current[strekenRef.current.length - 1]
    if (!streek) return
    const point = getPoint(e)
    const last = streek[streek.length - 1]
    if (last) {
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = strokeColor
      ctx.lineWidth = strokeWidth
      ctx.beginPath()
      ctx.moveTo(last.x, last.y)
      ctx.lineTo(point.x, point.y)
      ctx.stroke()
    }
    streek.push(point)
  }

  function onPointerEnd() {
    if (drawingRef.current) forceRerender((n) => n + 1)
    drawingRef.current = false
  }

  function clear() {
    strekenRef.current = []
    tekenAlles()
    meldWijziging()
  }

  function undo() {
    if (strekenRef.current.length === 0) return
    strekenRef.current = strekenRef.current.slice(0, -1)
    tekenAlles()
    meldWijziging()
  }

  function inktBreedte(): number {
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const s of strekenRef.current) {
      for (const p of s) {
        if (p.x < min) min = p.x
        if (p.x > max) max = p.x
      }
    }
    return Number.isFinite(min) ? Math.max(0, max - min) + strokeWidth : 0
  }

  useImperativeHandle(ref, (): SignaturePadHandle => ({
    clear,
    toDataURL: () => canvasRef.current?.toDataURL('image/png') ?? '',
    isEmpty: () => strekenRef.current.length === 0,
    undo,
    aantalStreken: () => strekenRef.current.length,
    inktBreedte,
  }), [])

  const leeg = strekenRef.current.length === 0

  return (
    <div className={cn('rounded-md border border-brand-border bg-white', className)}>
      <div className="relative">
        <canvas
          ref={canvasRef}
          role="img"
          aria-label={ariaLabel}
          // Pointer-events: none zou drawing breken; "touch-none" voorkomt dat
          // de browser scrollt tijdens het tekenen op mobiel.
          className={cn('block w-full touch-none', disabled && 'opacity-60')}
          style={{ height: `${height}px` }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerLeave={onPointerEnd}
          onPointerCancel={onPointerEnd}
        />
        {basislijn && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-6 border-b border-brand-border"
            style={{ bottom: `${Math.round(height * 0.28)}px` }}
          />
        )}
        {label && leeg && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 text-center text-sm text-brand-text-muted"
            style={{ bottom: `${Math.round(height * 0.28) + 8}px` }}
          >
            {label}
          </span>
        )}
      </div>
      {toonVoetbalk && (
        <div className="flex items-center justify-between border-t border-brand-border bg-brand-surface-alt px-3 py-2">
          <span className="text-xs text-brand-text-muted">
            {leeg ? 'Teken hierboven met je vinger of stylus' : 'Klaar om in te dienen'}
          </span>
          <button
            type="button"
            className="text-xs font-medium text-brand-donker underline-offset-2 hover:underline"
            onClick={clear}
          >
            Wissen
          </button>
        </div>
      )}
    </div>
  )
})
